/**
 * Where practice audio is played from. Default: stream /api/media/<id>/<rev>/orig (Range/206).
 * Milestone 5 installs a resolver for offline packs that returns a blob: URL made from the cached
 * file (or null to fall back to the network). The player revokes blob: URLs it received only after
 * the element's src changed and 'emptied' fired — resolvers must hand out a fresh URL per call.
 */
import type { SongFileDTO } from '../../shared/dto/library';
import { mediaUrl } from '../../shared/dto/offline';

export type AudioSourceResolver = (file: SongFileDTO) => Promise<string | null>;

let resolver: AudioSourceResolver | null = null;

export function setAudioSourceResolver(fn: AudioSourceResolver | null): void {
  resolver = fn;
}

export function networkAudioUrl(file: Pick<SongFileDTO, 'id' | 'rev'>): string {
  return mediaUrl(file.id, file.rev, 'orig');
}

/** Synchronous answer when no resolver is installed (keeps play() inside the user gesture). */
export function resolveAudioSourceSync(file: SongFileDTO): string | null {
  return resolver ? null : networkAudioUrl(file);
}

export async function resolveAudioSource(file: SongFileDTO, override?: AudioSourceResolver): Promise<string> {
  const fn = override ?? resolver;
  if (fn) {
    try {
      const url = await fn(file);
      if (url) return url;
    } catch {
      /* fall back to the network */
    }
  }
  return networkAudioUrl(file);
}
